export const MAX_RENDER_DEPTH = 20;

// Colours are CSS custom properties so light-mode can override them on
// .graph-view-light without touching JS.  Fallbacks are the dark-mode values.
export const COLORS = {
  variable:  'var(--pl-variable,  #9cdcfe)',
  number:    'var(--pl-number,    #b5cea8)',
  atom:      'var(--pl-atom,      #dcdcaa)',
  keyword:   'var(--pl-keyword,   #569cd6)',
  cut:       'var(--pl-cut,       #c586c0)',
  operator:  'var(--pl-operator,  #d4d4d4)',
  bracket:   'var(--pl-bracket,   #ffd700)',
  comma:     'var(--pl-comma,     #808080)',
  semicolon: 'var(--pl-semicolon, #c586c0)',
  pipe:      'var(--pl-pipe,      #569cd6)',
};

// Operators rendered infix with two children
const INFIX_OPS = new Set(['+', '-', '*', '/', 'is', '=', '\\=', '=:=', '<', '>', '=<', '>=']);

function tok(text, color, sub = false) {
  return { text, color, sub };
}

// Follow bound-variable chain and return the resolved term index.
function resolveIdx(idx, termVec) {
  let term = termVec[idx];
  while (term && term.termType === 'VARIABLE' && term.isBound) {
    idx = term.boundTo;
    term = termVec[idx];
  }
  return idx;
}

function resolvedTerm(idx, termVec) {
  return termVec[resolveIdx(idx, termVec)];
}

// True if term at idx is a conjunction or disjunction (needs parens when nested).
function isCompoundOp(idx, termVec, idVec) {
  const t = resolvedTerm(idx, termVec);
  if (!t || t.termType !== 'STRUCTURE') return false;
  const name = idVec[t.identifierIdx];
  return name === ',' || name === ';';
}

function isDisjunction(idx, termVec, idVec) {
  const t = resolvedTerm(idx, termVec);
  if (!t || t.termType !== 'STRUCTURE') return false;
  return idVec[t.identifierIdx] === ';';
}

// True if term at idx is an infix or compound operator (needs parens inside infix).
function needsParens(idx, termVec, idVec) {
  const t = resolvedTerm(idx, termVec);
  if (!t || t.termType !== 'STRUCTURE') return false;
  const name = idVec[t.identifierIdx];
  return INFIX_OPS.has(name) || name === ',' || name === ';';
}

// True if the term is a negative integer literal — needs parens next to arithmetic operators.
function isNegativeInteger(idx, termVec) {
  const t = resolvedTerm(idx, termVec);
  return t?.termType === 'INTEGER' && t.integerValue < 0;
}

// Parens needed when this term appears as a child of an arithmetic infix operator.
function needsArithParens(idx, termVec, idVec) {
  return needsParens(idx, termVec, idVec) || isNegativeInteger(idx, termVec);
}

function wrap(tokens) {
  return [tok('(', COLORS.bracket), ...tokens, tok(')', COLORS.bracket)];
}

function varTokens(term, varIdVec) {
  const rawName = term.varIdentifierIdx >= 0 ? varIdVec[term.varIdentifierIdx] : '_';
  const tokens = [tok(rawName, COLORS.variable)];
  if (term.varVersion !== 0) tokens.push(tok(String(term.varVersion), COLORS.variable, true));
  return tokens;
}

// Main entry point. Returns an array of { text, color, sub } tokens.
// When `bindings` is a Map (explicit mode), bound variables are rendered as unbound
// and an entry is added to the map: term_idx → { nameTokens, valueTokens }.
export function renderTerm(idx, termVec, idVec, varIdVec, depth = MAX_RENDER_DEPTH, bindings = null) {
  if (depth <= 0) return [tok('...', '#888')];
  if (idx < 0 || idx >= termVec.length) return [tok('?', '#888')];

  const term = termVec[idx];
  if (!term) return [tok('?', '#888')];

  // Explicit mode: intercept bound variables before resolution.
  if (bindings !== null && term.termType === 'VARIABLE' && term.isBound) {
    if (term.varIdentifierIdx >= 0 && !bindings.has(idx)) {
      const resolvedIdx = resolveIdx(idx, termVec);
      bindings.set(idx, {
        nameTokens: varTokens(term, varIdVec),
        // Value is rendered inline (full resolution, no further interception).
        valueTokens: renderTerm(resolvedIdx, termVec, idVec, varIdVec, depth - 1, null),
      });
    }
    return varTokens(term, varIdVec);
  }

  const ri = resolveIdx(idx, termVec);
  const t = termVec[ri];
  if (!t) return [tok('?', '#888')];

  switch (t.termType) {
    case 'INTEGER':
      return [tok(String(t.integerValue), COLORS.number)];

    case 'VARIABLE':
      return varTokens(t, varIdVec);

    case 'STRUCTURE':
      return renderStructure(t, termVec, idVec, varIdVec, depth, bindings);

    default:
      return [tok('?', '#888')];
  }
}

// Explicit-mode entry point. Populates the provided (or fresh) Map and returns { tokens, bindings }.
// Pass a shared Map across multiple calls to deduplicate bindings across children.
export function renderTermExplicit(idx, termVec, idVec, varIdVec, bindings = new Map()) {
  const tokens = renderTerm(idx, termVec, idVec, varIdVec, MAX_RENDER_DEPTH, bindings);
  return { tokens, bindings };
}

function renderStructure(term, termVec, idVec, varIdVec, depth, bindings) {
  const name = idVec[term.identifierIdx] ?? '?';
  const ch   = term.children;
  const n    = ch.length;

  // Empty list
  if (name === '[]' && n === 0)
    return [tok('[', COLORS.bracket), tok(']', COLORS.bracket)];

  // Cons cell — render as list
  if (name === '.' && n === 2)
    return renderList(term, termVec, idVec, varIdVec, depth, bindings);

  // Cut
  if (name === '!' && n === 0)
    return [tok('!', COLORS.cut)];

  // Conjunction
  if (name === ',')
    return renderConjunction(ch, termVec, idVec, varIdVec, depth, bindings);

  // Disjunction
  if (name === ';')
    return renderDisjunction(ch, termVec, idVec, varIdVec, depth, bindings);

  // Negation-as-failure (prefix unary)
  if (name === '\\+' && n === 1)
    return [tok('\\+', COLORS.operator), tok(' ', COLORS.operator),
            ...renderTerm(ch[0], termVec, idVec, varIdVec, depth - 1, bindings)];

  // Unary minus / plus
  if ((name === '-' || name === '+') && n === 1) {
    const childTokens = renderTerm(ch[0], termVec, idVec, varIdVec, depth - 1, bindings);
    const child = needsArithParens(ch[0], termVec, idVec) ? wrap(childTokens) : childTokens;
    return [tok(name, COLORS.operator), ...child];
  }

  // Infix binary operators
  if (INFIX_OPS.has(name) && n === 2)
    return renderInfix(ch, name, termVec, idVec, varIdVec, depth, bindings);

  // Zero-arity atom
  if (n === 0)
    return [tok(name, COLORS.atom)];

  // General compound: functor(a, b, ...)
  const tokens = [tok(name, COLORS.atom), tok('(', COLORS.bracket)];
  for (let i = 0; i < n; i++) {
    if (i > 0) tokens.push(tok(', ', COLORS.comma));
    tokens.push(...renderTerm(ch[i], termVec, idVec, varIdVec, depth - 1, bindings));
  }
  tokens.push(tok(')', COLORS.bracket));
  return tokens;
}

// Conjunction: each child wrapped in parens only if it is a disjunction (lower precedence).
function renderConjunction(ch, termVec, idVec, varIdVec, depth, bindings) {
  const tokens = [];
  for (let i = 0; i < ch.length; i++) {
    if (i > 0) tokens.push(tok(', ', COLORS.comma));
    const childTokens = renderTerm(ch[i], termVec, idVec, varIdVec, depth - 1, bindings);
    tokens.push(...(isDisjunction(ch[i], termVec, idVec) ? wrap(childTokens) : childTokens));
  }
  return tokens;
}

// Disjunction: each child wrapped in parens if it is any compound op (matches backend).
function renderDisjunction(ch, termVec, idVec, varIdVec, depth, bindings) {
  const tokens = [];
  for (let i = 0; i < ch.length; i++) {
    if (i > 0) tokens.push(tok(' ; ', COLORS.semicolon));
    const childTokens = renderTerm(ch[i], termVec, idVec, varIdVec, depth - 1, bindings);
    tokens.push(...(isCompoundOp(ch[i], termVec, idVec) ? wrap(childTokens) : childTokens));
  }
  return tokens;
}

// Infix: A op B — wraps children in parens if they are infix, compound, or negative integers.
function renderInfix(ch, op, termVec, idVec, varIdVec, depth, bindings) {
  const leftTokens  = renderTerm(ch[0], termVec, idVec, varIdVec, depth - 1, bindings);
  const rightTokens = renderTerm(ch[1], termVec, idVec, varIdVec, depth - 1, bindings);
  const left  = needsArithParens(ch[0], termVec, idVec) ? wrap(leftTokens)  : leftTokens;
  const right = needsArithParens(ch[1], termVec, idVec) ? wrap(rightTokens) : rightTokens;
  const opColor = op === 'is' ? COLORS.keyword : COLORS.operator;
  return [...left, tok(` ${op} `, opColor), ...right];
}

// List: [H | T] iteratively — avoids recursion on the tail.
function renderList(startTerm, termVec, idVec, varIdVec, depth, bindings) {
  const tokens = [tok('[', COLORS.bracket)];

  // Head: wrap in parens if it is a compound op (matches C++ log_list behaviour).
  const headIsCompound = isCompoundOp(startTerm.children[0], termVec, idVec);
  const headTokens = renderTerm(startTerm.children[0], termVec, idVec, varIdVec, depth - 1, bindings);
  tokens.push(...(headIsCompound ? wrap(headTokens) : headTokens));

  let nextIdx = startTerm.children[1];
  let d = depth;

  while (d-- > 0) {
    // Explicit mode: don't resolve a bound variable tail — show it as a variable.
    if (bindings !== null) {
      const rawTail = termVec[nextIdx];
      if (rawTail && rawTail.termType === 'VARIABLE' && rawTail.isBound) {
        tokens.push(tok(' | ', COLORS.pipe));
        tokens.push(...renderTerm(nextIdx, termVec, idVec, varIdVec, d, bindings));
        tokens.push(tok(']', COLORS.bracket));
        return tokens;
      }
    }

    nextIdx = resolveIdx(nextIdx, termVec);
    const nextTerm = termVec[nextIdx];
    if (!nextTerm) break;

    if (nextTerm.termType === 'STRUCTURE') {
      const nextName = idVec[nextTerm.identifierIdx];

      if (nextName === '[]') {
        tokens.push(tok(']', COLORS.bracket));
        return tokens;
      }

      if (nextName === '.') {
        tokens.push(tok(', ', COLORS.comma));
        const elemIsCompound = isCompoundOp(nextTerm.children[0], termVec, idVec);
        const elemTokens = renderTerm(nextTerm.children[0], termVec, idVec, varIdVec, d, bindings);
        tokens.push(...(elemIsCompound ? wrap(elemTokens) : elemTokens));
        nextIdx = nextTerm.children[1];
        continue;
      }
    }

    // Non-cons tail (partial list or variable)
    tokens.push(tok(' | ', COLORS.pipe));
    tokens.push(...renderTerm(nextIdx, termVec, idVec, varIdVec, d, bindings));
    break;
  }

  tokens.push(tok(']', COLORS.bracket));
  return tokens;
}
